import React, { useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import { FiDownload, FiSearch } from "react-icons/fi";
import toast from "react-hot-toast";
import { PageHeader, StateBlock, Skeleton } from "../../components/UI";
import Dialog from "../../components/UI/Dialog";
import { useLanguage } from "../../contexts/LanguageContext";
import { useFormat } from "../../hooks/useFormat";
import { levelFromElo } from "../../lib/authUser";
import { apiFetch } from "../../lib/api";
import { Action, Field } from "../MVP/shared";
import { Chip, Identity, Pager, Rows, Surface, Toolbar, adminErrorText } from "./ui";

type Row = { id: string; username: string; email: string; elo: number; isAdmin: boolean; premium: boolean; createdAt: string };
const PAGE = 50;

const List = styled(Rows)`
  > li {
    grid-template-columns: minmax(0, 2fr) minmax(170px, 1fr) minmax(120px, 1fr) minmax(120px, 0.8fr) minmax(110px, 0.8fr) auto;
  }
  > li.head {
    min-height: 36px;
    font-size: 12px;
    font-weight: 600;
    color: ${(p) => p.theme.colors.textMuted};
    &:hover {
      background: transparent;
    }
  }
  @media (max-width: 1100px) {
    > li {
      grid-template-columns: minmax(0, 1fr) auto;
    }
    > li.head {
      display: none;
    }
    .col-profile,
    .col-sub,
    .col-role,
    .col-reg {
      display: none;
    }
    .inline {
      display: block;
    }
  }
  .inline {
    display: none;
    font-size: 13px;
    color: ${(p) => p.theme.colors.textMuted};
    margin-top: 2px;
  }
`;
const Dl = styled.dl`
  display: grid;
  gap: 12px;
  div {
    display: grid;
    grid-template-columns: 140px minmax(0, 1fr);
    gap: 12px;
  }
  dt {
    font-size: 14px;
    color: ${(p) => p.theme.colors.textMuted};
  }
  dd {
    font-size: 15px;
    overflow-wrap: anywhere;
  }
  @media (max-width: 480px) {
    div {
      grid-template-columns: minmax(0, 1fr);
      gap: 2px;
    }
  }
`;

export default function Users() {
  const { t } = useLanguage();
  const f = useFormat();
  const [rows, setRows] = useState<Row[] | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [q, setQ] = useState("");
  const [error, setError] = useState("");
  const [pick, setPick] = useState<Row | null>(null);

  useEffect(() => {
    const id = window.setTimeout(() => setQ(search.trim()), 400);
    return () => window.clearTimeout(id);
  }, [search]);
  useEffect(() => setPage(1), [q]);

  const load = useCallback(async () => {
    setError("");
    try {
      const params = new URLSearchParams({ page: String(page), pageSize: String(PAGE) });
      if (q) params.set("q", q);
      const res = await apiFetch(`/admin/users?${params}`);
      if (!res.ok) throw new Error(String(res.status));
      const data = await res.json();
      setRows(
        (data.items || []).map((r: Record<string, unknown>) => ({
          id: String(r.id),
          username: String(r.username || ""),
          email: String(r.email || ""),
          elo: Number(r.elo) || 0,
          isAdmin: Boolean(r.is_admin),
          premium: Boolean(r.is_premium),
          createdAt: String(r.created_at || ""),
        })),
      );
      setTotal(Number(data.total) || 0);
    } catch (e) {
      setRows(null);
      setError((e as Error).message || "error");
    }
  }, [page, q]);
  useEffect(() => {
    setRows(null);
    void load();
  }, [load]);

  const exportPage = () => {
    if (!rows?.length) return;
    const header = ["id", "username", "email", "elo", "is_admin", "is_premium", "created_at"];
    const lines = [header.join(",")];
    for (const u of rows) lines.push([u.id, JSON.stringify(u.username), JSON.stringify(u.email), u.elo, u.isAdmin, u.premium, u.createdAt].join(","));
    const url = URL.createObjectURL(new Blob(["﻿" + lines.join("\r\n")], { type: "text/csv;charset=utf-8" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `users-page-${page}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };
  const pages = Math.max(1, Math.ceil(total / PAGE));

  return (
    <>
      <PageHeader
        title={t("adm.users")}
        actions={
          <>
            <Action type="button" onClick={exportPage} disabled={!rows?.length}>
              <FiDownload aria-hidden="true" />
              {t("adm.export_page")}
            </Action>
            <Action type="button" onClick={load}>
              {t("adm.refresh")}
            </Action>
          </>
        }
      />
      <Surface aria-label={t("adm.users")}>
        <Toolbar>
          <label className="grow" style={{ position: "relative" }}>
            <FiSearch aria-hidden="true" style={{ position: "absolute", left: 14, top: 16, opacity: 0.6 }} />
            <Field type="search" aria-label={t("adm.search_users")} placeholder={t("adm.search_users")} value={search} onChange={(e) => setSearch(e.target.value)} style={{ paddingLeft: 40 }} />
          </label>
          <span className="count">{t("adm.total", { n: total })}</span>
        </Toolbar>
        {error ? (
          <StateBlock kind="error" title={t("adm.load_error")} text={adminErrorText(error, t)} actionLabel={t("adm.retry")} onAction={load} />
        ) : !rows ? (
          <div role="status" aria-label={t("adm.loading")} style={{ display: "grid", gap: 8 }}>
            {[0, 1, 2, 3, 4].map((i) => (
              <Skeleton key={i} $h={56} $r={10} />
            ))}
          </div>
        ) : rows.length === 0 ? (
          <StateBlock kind="empty" title={t("adm.no_users")} />
        ) : (
          <List>
            <li className="head" aria-hidden="true">
              <span>{t("adm.col_user")}</span>
              <span>{t("adm.col_profile")}</span>
              <span>{t("adm.col_sub")}</span>
              <span>{t("adm.col_role")}</span>
              <span>{t("adm.col_reg")}</span>
              <span />
            </li>
            {rows.map((u) => (
              <li key={u.id}>
                <div>
                  <Identity name={u.username || "—"} sub={u.email || t("adm.no_email")} />
                  <div className="inline">
                    {t("adm.level", { n: levelFromElo(u.elo) })} · {u.elo} ELO · {u.premium ? t("adm.premium_on") : t("adm.premium_off")}
                  </div>
                </div>
                <span className="col-profile num">
                  {t("adm.level", { n: levelFromElo(u.elo) })} · {u.elo} ELO
                </span>
                <span className="col-sub">{u.premium ? <Chip $tone="ok">{t("adm.premium_on")}</Chip> : <span className="sub">{t("adm.premium_off")}</span>}</span>
                <span className="col-role">{u.isAdmin ? <Chip $tone="info">{t("adm.role_admin")}</Chip> : <span className="sub">{t("adm.role_user")}</span>}</span>
                <span className="col-reg num">{u.createdAt ? f.date(u.createdAt) : "—"}</span>
                <Action type="button" $variant="ghost" onClick={() => setPick(u)} aria-label={`${t("adm.details")}: ${u.username}`}>
                  {t("adm.details")}
                </Action>
              </li>
            ))}
          </List>
        )}
        {rows && pages > 1 && (
          <Pager>
            <span>{t("common.page_of", { current: page, total: pages })}</span>
            <span className="btns">
              <Action type="button" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                {t("common.back")}
              </Action>
              <Action type="button" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>
                {t("common.next")}
              </Action>
            </span>
          </Pager>
        )}
      </Surface>
      {pick && (
        <Dialog
          size="md"
          label={pick.username}
          onClose={() => setPick(null)}
          footer={
            <>
              <Action
                type="button"
                onClick={() =>
                  navigator.clipboard
                    .writeText(pick.id)
                    .then(() => toast.success(t("adm.copied")))
                    .catch(() => toast.error(t("match.copy_failed")))
                }
              >
                {t("adm.copy_id")}
              </Action>
              <Action type="button" $variant="primary" onClick={() => setPick(null)}>
                {t("adm.close")}
              </Action>
            </>
          }
        >
          <h2>{pick.username}</h2>
          <Dl>
            <div>
              <dt>{t("adm.id")}</dt>
              <dd>{pick.id}</dd>
            </div>
            <div>
              <dt>Email</dt>
              <dd>{pick.email || t("adm.no_email")}</dd>
            </div>
            <div>
              <dt>{t("adm.col_profile")}</dt>
              <dd>
                {t("adm.level", { n: levelFromElo(pick.elo) })} · {pick.elo} ELO
              </dd>
            </div>
            <div>
              <dt>{t("adm.col_sub")}</dt>
              <dd>{pick.premium ? t("adm.premium_on") : t("adm.premium_off")}</dd>
            </div>
            <div>
              <dt>{t("adm.col_role")}</dt>
              <dd>{pick.isAdmin ? t("adm.role_admin") : t("adm.role_user")}</dd>
            </div>
            <div>
              <dt>{t("adm.col_reg")}</dt>
              <dd>{pick.createdAt ? f.date(pick.createdAt) : "—"}</dd>
            </div>
          </Dl>
          <p className="sub" style={{ fontSize: 13, opacity: 0.75 }}>
            {t("adm.user_note")}
          </p>
        </Dialog>
      )}
    </>
  );
}
